import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../hooks/useTheme';
export function About() {
  const { isDark } = useTheme();
  return (
    <section id="about" className="relative py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ">
          {/* Left: Depth-layered image frame */}
          <motion.div
            initial={{
              opacity: 0,
              x: -40
            }}
            whileInView={{
              opacity: 1,
              x: 0
            }}
            viewport={{
              once: true,
              margin: '-50px'
            }}
            transition={{
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1]
            }}
            className="relative">

            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Back layer */}
              <div
                className={`absolute -top-4 -left-4 w-full h-full rounded-2xl ${isDark ? 'bg-violet-600/20 border border-violet-500/20' : 'bg-violet-100 border border-violet-200'}`} />

              {/* Middle layer */}
              <div
                className={`absolute -top-2 -left-2 w-full h-full rounded-2xl ${isDark ? 'bg-indigo-600/20 border border-indigo-500/20' : 'bg-indigo-100 border border-indigo-200'}`} />

              {/* Front layer - main content */}
              <div
                className={`relative rounded-2xl overflow-hidden ${isDark ? 'bg-gradient-to-br from-indigo-900/60 via-violet-900/40 to-slate-900/80 border border-white/[0.08]' : 'bg-gradient-to-br from-indigo-50 via-violet-50 to-white border border-slate-200'}`}>

                <div className="p-10 md:p-14">
                  {/* Abstract fintech graphic */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-3 rounded-full flex-1 ${isDark ? 'bg-indigo-500/40' : 'bg-indigo-200'}`} />

                      <div
                        className={`h-3 rounded-full w-20 ${isDark ? 'bg-violet-500/30' : 'bg-violet-200'}`} />

                    </div>
                    <div
                      className={`h-24 rounded-xl ${isDark ? 'bg-white/[0.04] border border-white/[0.06]' : 'bg-white border border-slate-100'}`}>

                      <div className="p-4 flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-100'}`} />

                        <div className="flex-1 space-y-2">
                          <div
                            className={`h-2.5 rounded w-2/3 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />

                          <div
                            className={`h-2 rounded w-1/2 ${isDark ? 'bg-white/[0.06]' : 'bg-slate-100'}`} />

                        </div>
                        <div
                          className={`text-lg font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>

                          +$4,280
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div
                        className={`h-16 rounded-xl flex-1 ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white border border-slate-100'}`} />

                      <div
                        className={`h-16 rounded-xl flex-1 ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white border border-slate-100'}`} />

                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-3 rounded-full w-16 ${isDark ? 'bg-emerald-500/30' : 'bg-emerald-200'}`} />

                      <div
                        className={`h-3 rounded-full flex-1 ${isDark ? 'bg-white/[0.06]' : 'bg-slate-100'}`} />

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Typography and story */}
          <motion.div
            initial={{
              opacity: 0,
              x: 40
            }}
            whileInView={{
              opacity: 1,
              x: 0
            }}
            viewport={{
              once: true,
              margin: '-50px'
            }}
            transition={{
              type: 'spring',
              stiffness: 80,
              damping: 20,
              delay: 0.15
            }}>

            <span
              className={`inline-block text-sm font-semibold tracking-wider uppercase mb-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>

              About 1401
            </span>
            <h2
              className={`text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-6 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>

              Built by engineers,{' '}
              <span className="gradient-text">designed for scale</span>
            </h2>
            <div
              className={`space-y-5 text-base leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>

              <p>
                Founded in 2019, 1401 was born from a simple frustration:
                payment infrastructure shouldn't be the bottleneck for ambitious
                businesses. Our founding team of engineers from leading fintech
                companies set out to build the payment platform they always
                wished existed.
              </p>
              <p>
                Today, we process over $12 billion annually for thousands of
                businesses across 150+ countries. From two-person startups to
                Fortune 500 enterprises, our infrastructure scales seamlessly
                with your growth.
              </p>
              <p>
                Our mission remains unchanged: make money movement as simple,
                fast, and reliable as sending an email. We're backed by $180M in
                funding from top-tier investors and a team of 300+ across San
                Francisco, London, and Singapore.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-8">
              <div>
                <div
                  className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>

                  300+
                </div>
                <div
                  className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>

                  Team Members
                </div>
              </div>
              <div>
                <div
                  className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>

                  $180M
                </div>
                <div
                  className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>

                  Total Funding
                </div>
              </div>
              <div>
                <div
                  className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>

                  2019
                </div>
                <div
                  className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>

                  Founded
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

}